import React, { useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { isEmpty } from "lodash";
import { IoMdSettings } from "react-icons/io";
import AuthContext from "../../context/AuthContext";

// Shared desktop three-column shell (top nav + left Sports sidebar + center
// content + right Open Bets dock). It is opt-in: the nav/sidebar/dock carry the
// `home-desktop-only` marker plus a `home-desktop-*` class whose display rules
// live inside the `@media (min-width: 1024px)` block in theme.css, so on mobile
// only the `children` flow through as a plain block (unchanged mobile UI).
//
// The right column is just the `#desktop-open-bets` dock target — the globally
// mounted <Openbets /> (via All_overlay) portals into it automatically whenever
// this shell is present, so no Open Bets logic lives here. Pages can pass extra
// markup via `rightTop` to sit ABOVE the portalled Open Bets panel (e.g. Match
// Details docks its Live Streaming bar there); portal content always appends
// after React children, so the order is rightTop → Open Bets.

// Nav bar sport entries (order matches the reference top bar).
const NAV_SPORTS = [
  "Cricket",
  "Vimaan",
  "Tennis",
  "Soccer",
  "Horse Racing",
  "Greyhound Racing",
  "Basketball",
  "Lottery",
  "Live Casino",
  "Tips & Previews",
];

// Left sidebar sport rows.
const SIDEBAR_SPORTS = [
  "Cricket",
  "Casino",
  "Tennis",
  "Soccer",
  "Horse Racing",
  "Greyhound Racing",
  "Basketball",
  "Lottery",
];

const DesktopShell = ({ children, activeNav = "Home", onSportSelect, rightTop }) => {
  const navigate = useNavigate();
  const { user, setShowMiniCasino } = useContext(AuthContext);

  // When a page supplies onSportSelect (Home), sport clicks are handled by the
  // page (e.g. filtering its center). Otherwise the shell navigates like the
  // reference site: exchange sports go to Home, casino-style entries open the
  // mini-casino, and the rest fall back to the Sports page.
  const goSport = (sport) => {
    if (onSportSelect) {
      onSportSelect(sport);
      return;
    }
    if (["Cricket", "Soccer", "Tennis"].includes(sport)) {
      navigate("/");
    } else if (["Horse Racing", "Greyhound Racing", "Basketball"].includes(sport)) {
      navigate("/inplay");
    } else if (["Live Casino", "Casino"].includes(sport)) {
      navigate("/live-casino");
    } else if (["Lottery", "Vimaan"].includes(sport)) {
      if (isEmpty(user)) navigate("/login");
      else setShowMiniCasino(true);
    } else {
      navigate("/");
    }
  };

  return (
    <>
      <nav className="home-desktop-nav home-desktop-only" aria-label="Desktop navigation">
        <div className="desktop-nav-links">
          {[
            ["Home", () => navigate("/")],
            ["In-Play", () => navigate("/inplay")],
            ["Multi Markets", () => (isEmpty(user) ? navigate("/login") : navigate("/multiMarket"))],
            ...NAV_SPORTS.map((sport) => [sport, () => goSport(sport)]),
          ].map(([label, action]) => (
            <button
              type="button"
              key={label}
              onClick={action}
              className={
                label === activeNav ? "is-current" : label === "Vimaan" ? "desktop-vimaan" : ""
              }
            >
              {label === "Vimaan" && <img src="/home/aviator.png" alt="" />}
              {label === "Vimaan" ? <span className="desktop-vimaan-label">Vimaan</span> : label}
            </button>
          ))}
        </div>
        {!isEmpty(user) && (
          <Link
            to="/settings"
            className={activeNav === "Setting" ? "desktop-nav-setting is-current" : "desktop-nav-setting"}
          >
            Setting <IoMdSettings aria-hidden="true" />
          </Link>
        )}
      </nav>

      <div className="home-desktop-columns">
        <aside className="home-desktop-sports home-desktop-only" aria-label="Sports">
          <h2>
            <svg
              className="desktop-sports-menu-dots"
              width="4"
              height="14"
              viewBox="0 0 6 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <rect width="6" height="5" rx="1.5" />
              <rect y="7.5" width="6" height="5" rx="1.5" />
              <rect y="15" width="6" height="5" rx="1.5" />
            </svg>
            Sports
          </h2>
          {SIDEBAR_SPORTS.map((sport) => (
            <button type="button" key={sport} onClick={() => goSport(sport)}>
              {sport}
              <span className="desktop-sport-arrow" aria-hidden="true">
                <svg width="12" height="16" viewBox="0 0 12 16" fill="none">
                  <path
                    d="M4 3L9 8L4 13"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </button>
          ))}
        </aside>

        <div className="home-desktop-main">{children}</div>

        <aside
          id="desktop-open-bets"
          className="home-desktop-bets home-desktop-only"
          aria-label="Open Bets"
        >
          {rightTop}
        </aside>
      </div>
    </>
  );
};

export default DesktopShell;
